/*
 * THE PRODUCT PAGE'S OWN BEHAVIOUR — three things, all of them small, none of
 * them able to break the page if they fail.
 *
 *   1. Every below-fold CTA scrolls to a position where the SIZE SELECTOR and
 *      the ADD TO CART button are both inside the viewport at once.
 *   2. The delivery line gets a real date.
 *   3. The sticky bar's "change size" button uses (1) and then focuses the
 *      size control.
 *
 * IT IS DELEGATED ON `document` AND READS THE DOM AT CLICK TIME, NEVER AT LOAD
 * TIME. Horizon refetches and swaps the whole product-information section on
 * every variant change, so anything holding an element reference from load is
 * holding a node that is no longer in the document. Every lookup below happens
 * inside the handler for that reason.
 *
 * NOTHING HERE IS REQUIRED FOR THE PAGE TO WORK. The CTAs are ordinary anchors
 * to `#rz-buy`, so with this file absent the browser still jumps to the size
 * selector; the delivery sentence renders its duration in Liquid and is true
 * without a date. This file makes both better, and its absence is a worse page
 * rather than a broken one.
 */
(function () {
  'use strict';

  var ANCHOR = 'rz-buy';
  var PAD = 12;

  function reduced() {
    return window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── how much of the top of the viewport is not ours ─────────────────────
   * A sticky or fixed header covers the top of the viewport, and scrolling an
   * element to y=0 puts it UNDER that header rather than in view. Measured at
   * call time rather than assumed, because the header's height changes with
   * the announcement bar and with the width. */
  function headerHeight() {
    var best = 0;
    var els = document.querySelectorAll('header, .header, [class*="header-group"] > *');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      var r = el.getBoundingClientRect();
      if (r.top <= 1 && r.height > best && r.height < window.innerHeight * 0.4) best = r.height;
    }
    return Math.round(best);
  }

  /* The SIZE control, not the whole picker. Shape is a row of buttons above it
   * and including it can push the pair past a short viewport for no gain --
   * the brief names the size selector and the button. Falls back outward:
   * the size fieldset, then the picker, so a template that renders sizes as
   * buttons instead of a dropdown still resolves to something real. */
  function sizeControl(scope) {
    var picker = scope.querySelector('.variant-picker');
    if (!picker) return null;
    var sel = picker.querySelector('select');
    if (sel) return sel;
    var fieldsets = picker.querySelectorAll('fieldset, .variant-option');
    if (fieldsets.length) return fieldsets[fieldsets.length - 1];
    return picker;
  }

  function buyButton(scope) {
    return scope.querySelector('.buy-buttons-block .add-to-cart-button')
        || scope.querySelector('.add-to-cart-button')
        || scope.querySelector('.buy-buttons-block');
  }

  /* ── THE TARGET IS COMPUTED FROM BOTH ELEMENTS, NOT FROM ONE ANCHOR ──────
   * "Scroll to the buy area" is not the requirement; "a person can see what
   * they are choosing and the button that acts on it, together" is. So the
   * band from the top of the size control to the bottom of the button is
   * measured and CENTRED in whatever vertical space the header leaves.
   *
   * WHEN THE BAND IS TALLER THAN THE VIEWPORT the button wins: it is put at
   * the bottom of the usable band and as much of the selector as fits sits
   * above it. That is the honest degradation -- the alternative, centring a
   * band that does not fit, hides the bottom of the button. */
  function buyTarget() {
    var scope = document.querySelector('.product-details') || document;
    var size = sizeControl(scope);
    var btn = buyButton(scope);
    if (!size || !btn) return null;

    var y = window.pageYOffset || document.documentElement.scrollTop;
    var top = size.getBoundingClientRect().top + y;
    var bottom = btn.getBoundingClientRect().bottom + y;

    var headTop = headerHeight();
    var usable = window.innerHeight - headTop;
    var need = bottom - top;

    var target;
    if (need + PAD * 2 <= usable) {
      target = top - headTop - (usable - need) / 2;
    } else {
      target = bottom + PAD - window.innerHeight;
    }

    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return { y: Math.min(Math.max(0, Math.round(target)), max), size: size };
  }

  function scrollToBuy(focusSize) {
    var t = buyTarget();
    if (!t) return false;
    window.scrollTo({ top: t.y, behavior: reduced() ? 'auto' : 'smooth' });
    if (focusSize && t.size && typeof t.size.focus === 'function') {
      /* AFTER the scroll, not during it. Focusing a control mid-scroll makes
       * the browser scroll it into view itself and fight the animation. */
      var settle = reduced() ? 0 : 500;
      setTimeout(function () {
        try { t.size.focus({ preventScroll: true }); } catch (e) { t.size.focus(); }
      }, settle);
    }
    return true;
  }
  /* `deliveryDate` is exported below, once it is defined. */

  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href], button[data-rz-tobuy]');
    if (!a) return;
    var wantsFocus = a.hasAttribute('data-rz-tobuy');
    if (!wantsFocus) {
      var href = a.getAttribute('href') || '';
      if (href.indexOf('#' + ANCHOR) === -1) return;
    }
    if (scrollToBuy(wantsFocus)) e.preventDefault();
  });

  /* ── THE DELIVERY DATE ───────────────────────────────────────────────────
   * FIVE DAYS FROM HANDOVER, COUNTING THE HANDOVER DAY -- not five calendar
   * days from the order. The parcel is handed to the courier on the next
   * WORKING day, and the count starts there, so `days` of 5 means
   * handover + 4.
   *
   * ── WHAT THIS REPLACED, AND WHERE THE SEVEN CAME FROM (2026-09-08) ──────
   * The previous rule was `today + 5`, then rolled forward off the weekend.
   * That is where the reported "seven days" lived, and it was ONE weekday:
   * a MONDAY order lands on the Saturday and rolls to the Monday a week out,
   * so the sentence read "in 7 days" -- and `deliveryPhrase` correctly refused
   * to name a weekday at 7, because a weekday name repeats every seven days
   * and had stopped identifying a date. Nothing was broken in the client; the
   * ARITHMETIC promised seven days against a policy that says five.
   *
   * ── WHY THIS RULE AND NOT ANOTHER ──────────────────────────────────────
   * It is the only one that reproduces BOTH figures the user gave. Checked
   * against every candidate before it was written:
   *
   *     order Sunday  -> arrives Friday      (the user's example)
   *     order Friday  -> arrives Tuesday     (the user's example)
   *
   *   today+5 rolled forward   Sun->Fri yes, Fri->Wed  no
   *   today+5 rolled backward  Sun->Fri yes, Fri->Wed  no
   *   5 business days          Sun->Fri yes, Fri->Fri  no
   *   handover+4               Sun->Fri yes, Fri->Tue  BOTH
   *
   * A Sunday order is handed over on the Monday and arrives Friday; a Friday
   * order is handed over the same day and arrives Tuesday. Those are the
   * user's two sentences exactly, from one rule, with no special case.
   *
   * ── IT ASSUMES SAME-DAY HANDOVER ON A WORKING DAY, AND THAT IS AN
   * ASSERTION RATHER THAN A MEASUREMENT. The Friday example only lands if a
   * Friday order is handed over on the Friday. No pick-and-pack cutoff is
   * recorded anywhere in this project, so there is no hour at which that
   * stops being true and none is invented here -- inventing one is the
   * urgency the constitution forbids. If a cutoff is ever measured it goes in
   * `data-cutoff-hour` and the branch below already applies it.
   *
   * THE ARRIVAL IS STILL ROLLED FORWARD OFF THE WEEKEND, because DHL does not
   * deliver residentially on a Saturday or Sunday without a surcharge we do
   * not buy. Rolling forward can only make the promise LATER, which is the
   * safe direction. Two weekdays reach six calendar days that way (Tuesday
   * and Saturday orders); none reaches seven. */
  function nextWorkingDay(d) {
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    return d;
  }

  function deliveryDate(days, cutoffHour, from) {
    var d = from ? new Date(from) : new Date();
    var extra = (typeof cutoffHour === 'number' && d.getHours() >= cutoffHour) ? 1 : 0;
    d.setHours(12, 0, 0, 0);                 /* midday: no DST edge on the add */
    d.setDate(d.getDate() + extra);
    nextWorkingDay(d);                       /* handover */
    d.setDate(d.getDate() + (days - 1));     /* the handover day is day 1 */
    return nextWorkingDay(d);                /* no residential weekend delivery */
  }

  /* ── THE SENTENCE PICKS ITS OWN FORM, AND THE RULE IS ARITHMETIC ─────────
   * "Order today and get it ON FRIDAY" while the weekday names exactly one
   * date; "Order today and get it IN 7 DAYS" once it stops doing so.
   *
   * THE BOUNDARY IS NOT A PREFERENCE. A weekday name repeats every seven days,
   * so it identifies a single date only while the arrival is 1 to 6 days away.
   * At 7 the name IS today's own name -- "order on Monday, get it Monday" --
   * and it has stopped being a date at all, so the duration is the only form
   * that still says something. Past 7 it is worse, not better.
   *
   * UNDER THE HANDOVER RULE ABOVE, ALL SEVEN WEEKDAYS NAME A DAY. The worst
   * case is six calendar days (a Tuesday or a Saturday order), which is inside
   * the 1-6 window, so the duration branch is now unreachable at `days` of 5 --
   * it is KEPT because it is reachable at other values of the setting and
   * because it is what makes the sentence safe if the rule is ever changed
   * back. That the branch no longer fires is a consequence of the arithmetic,
   * not something tuned for.
   *
   * IT COUNTS CALENDAR DAYS, NOT THE `days` SETTING, because the weekend roll
   * adds one or two to it. Both dates are flattened to midnight first so the
   * subtraction cannot be thrown by the hours or by a DST boundary. */
  function dayGap(from, to) {
    var a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    var b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((b - a) / 86400000);
  }

  function weekdayName(d) {
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        weekday: 'long'
      }).format(d);
    } catch (e) {
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
              'Thursday', 'Friday', 'Saturday'][d.getDay()];
    }
  }

  /* THE PREPOSITION TRAVELS WITH THE PHRASE, NOT WITH THE SETTING. "on" and
   * "in" belong to different forms of the same sentence, so a slot ending
   * "...get it on" could only ever be right half the time. The copy is "Order
   * today and get it" and `{date}` supplies "on Friday" or "in 7 days" whole --
   * which is also why the no-JavaScript fallback reads "in 5 days" rather than
   * a bare number. */
  function deliveryPhrase(d) {
    var gap = dayGap(new Date(), d);
    if (gap <= 0) return 'today';
    if (gap === 1) return 'tomorrow';
    if (gap <= 6) return 'on ' + weekdayName(d);
    return 'in ' + gap + ' days';
  }

  /* EXPORTED SO THE ARITHMETIC CAN BE CHECKED FROM OUTSIDE. The seven-weekday
   * table in the delivery report is generated by calling this with a fixed
   * `from`, on the live page, rather than by reimplementing the rule in a
   * probe -- a probe that reimplements the rule can only ever agree with
   * itself. */
  window.RugPDP = { scrollToBuy: scrollToBuy, buyTarget: buyTarget,
                    deliveryDate: deliveryDate, deliveryPhrase: deliveryPhrase };

  function isoDate(d) {
    function two(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate());
  }

  function paintDates(root) {
    var nodes = (root || document).querySelectorAll('[data-rz-eta]');
    for (var i = 0; i < nodes.length; i++) {
      var host = nodes[i];
      var slot = host.querySelector('[data-rz-eta-text]');
      if (!slot) continue;
      var tpl = host.getAttribute('data-template');
      if (tpl === null) {
        /* The Liquid has already substituted the fallback into the sentence,
         * so the TEMPLATE is not recoverable from the rendered text. It is
         * stashed on first paint, from the attribute the block writes. */
        tpl = host.getAttribute('data-sentence') || '';
        host.setAttribute('data-template', tpl);
      }
      if (!tpl) continue;
      var days = parseInt(host.getAttribute('data-days'), 10);
      if (!(days > 0)) continue;
      var cutoff = host.hasAttribute('data-cutoff-hour')
        ? parseInt(host.getAttribute('data-cutoff-hour'), 10) : null;
      var d = deliveryDate(days, typeof cutoff === 'number' && !isNaN(cutoff) ? cutoff : null);
      var phrase = deliveryPhrase(d);
      slot.textContent = tpl.replace('{date}', phrase);
      /* BOTH ARE RECORDED: the phrase because it is what the reader was shown,
       * and the ISO date because a phrase cannot be checked against a calendar.
       * The pair is what makes the sentence verifiable from outside. */
      host.setAttribute('data-resolved', phrase);
      host.setAttribute('data-date', isoDate(d));
    }
  }

  /* ── TWO THINGS DO NOT FOLLOW THE VARIANT ON THEIR OWN ────────────────────
   * MEASURED, AND IT CORRECTS A COMMENT THIS PROJECT HAS CARRIED FOR WEEKS.
   * "Horizon re-inserts the whole product-information section on every variant
   * change" is NOT what this version does. `variant-picker.js` morphs the
   * `<variant-picker>` element ALONE and dispatches `shopify:product:select`
   * carrying the new markup; every other part of the page updates itself by
   * LISTENING for that event and pulling its own node out of `detail.html` —
   * which is why `product-price.js`, `media-gallery.js`, `product-sku.js`,
   * `product-form.js` and `sticky-add-to-cart.js` all exist and all do the
   * same three lines.
   *
   * Two things on this page had no listener, so neither followed the size:
   *
   *   [data-rug-vis]   the visualiser's config — its own doc said it re-rendered
   *                    "for free". Measured after a size change: the price read
   *                    $564.27 and `data-vis` still carried the 60x120 label.
   *   .rz-pdp-terms    Shopify's instalment widget reads `shopify-meta` when it
   *                    connects, so even a morphed attribute would not re-price
   *                    it. The whole container is REPLACED, which gives a fresh
   *                    element and a fresh shadow root.
   *
   * `event.promise` rather than the event itself: the new markup is fetched
   * asynchronously and the event fires first. That is the contract every
   * Horizon component here uses. */
  function followVariant(root) {
    var section = root.closest ? root.closest('.shopify-section') : null;
    if (!section || section.__rzBound) return;
    section.__rzBound = true;
    section.addEventListener('shopify:product:select', function (event) {
      if (!event.promise || !event.promise.then) return;
      event.promise.then(function (r) {
        var html = r && r.detail && r.detail.html;
        if (!html) return;

        var live = document.querySelector('[data-rug-vis]');
        var fresh = html.querySelector('[data-rug-vis]');
        if (live && fresh) {
          var cfg = fresh.getAttribute('data-vis');
          if (cfg !== null) live.setAttribute('data-vis', cfg);
        }

        /* THE SHIPPING LINE follows the size (7 or 20 days, FD-0547): the section
         * Horizon rendered for the new variant carries the right one. */
        var eta = document.querySelector('[data-rz-eta]');
        var newEta = html.querySelector('[data-rz-eta]');
        if (eta && newEta) eta.replaceWith(newEta);

        var terms = document.querySelector('.rz-pdp-terms');
        var newTerms = html.querySelector('.rz-pdp-terms');
        if (terms && newTerms) terms.replaceWith(newTerms);
      }).catch(function () { /* a failed variant fetch is the picker's problem */ });
    });
  }

  function boot() {
    paintDates(document);
    var vp = document.querySelector('variant-picker');
    if (vp) followVariant(vp);
  }

  /* THE MOBILE THUMBNAIL-STRIP ARROWS ARE GONE (2026-09-09). `thumbsOf`,
   * `selectedIndex`, `syncThumbNav`, `watchSelection`, `bootThumbNav` and the
   * delegated click handler are DELETED with the markup they drove - the pair
   * moved onto the main image and is now the slideshow's own
   * `slideshow-arrows`, which `slideshow.js` already disables at the ends
   * (`infinite` is false) and already keeps the strip in step with, by
   * centring the selected thumbnail on every `set current`. There is nothing
   * for this file to synchronise any more. */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* The section is re-inserted on every variant change, so the date has to be
   * repainted onto the new node. A MutationObserver rather than a Horizon
   * event because it is one line and it cannot go stale when an event name
   * changes upstream. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var n = records[i].addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-rz-eta]')) { paintDates(n.parentNode); return; }
          if (n.querySelector && n.querySelector('[data-rz-eta]')) { paintDates(n); return; }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
